import classNames from "classnames";
import { ChangeEvent, FC, useCallback, useEffect, useId, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ReactComponent as NotFoundIcon } from "assets/images/common/not-found.svg";

import Button from "components/Button";
import SearchInput from "components/SearchInput";

import { AirQualityCardData } from "modules/assets/AirQualityCard";
import ComplianceTrendChart from "modules/assets/ComplianceTrendChart";
import IncidentBarChart from "modules/assets/IncidentBarChart";
import { buildMetricCardBySlug } from "modules/assets/OverviewTab/co2";
import { THERMAL_CARDS } from "modules/assets/OverviewTab/data";
import { buildHumidityCard, buildTemperatureCard } from "modules/assets/OverviewTab/thermal";
import { ThermalCardData } from "modules/assets/ThermalCard";
import StatCard, { StatCardData } from "modules/common/StatCard";
import AssetGroup, { AssetGroupCard, AssetGroupData } from "modules/portfolios/AssetGroup";

import { CreateAssetRequest } from "entities/assets";
import { AssetFormValues, BaseOption, ModalType } from "entities/common";
import { PortfolioComplianceScore, PortfolioIAQDashboard } from "entities/portfolios";

import { StatusFilter } from "./data";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { createAssetRequest, resetSuccessfullyAddedAsset } from "store/slices/assetSlice";
import { setModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { hideModal, showModal } from "store/slices/modalSlice";
import { getIAQDashboardRequest } from "store/slices/portfolioSlice";
import { showToast } from "store/slices/toastSlice";

import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";
import { PAGES } from "utils/links";

import useDebounce from "hooks/useDebounce";
import useRealtime from "hooks/useRealtime";

import styles from "./style.module.scss";

const TEMP_BASE = THERMAL_CARDS.find((card) => card.title === "Air temperature")!;
const HUM_BASE = THERMAL_CARDS.find((card) => card.title === "Relative humidity")!;

const formatImprovement = (improvement: number | null | undefined): string => {
  if (improvement == null) return "-";
  if (improvement > 0) return `+${improvement}%`;
  return `${improvement}%`;
};

type AdaptedAssetGroupCard = AssetGroupCard & {
  data: AirQualityCardData | ThermalCardData;
};

const adaptAssetGroupCard = (card: AssetGroupCard): AdaptedAssetGroupCard | null => {
  if (card.kind === "metric" && card.metric_card) {
    const data = buildMetricCardBySlug(card.metric, card.metric_card);
    return data ? { ...card, data } : null;
  }

  if (card.kind === "thermal" && card.thermal_card) {
    return {
      ...card,
      data: buildTemperatureCard(TEMP_BASE, card.thermal_card),
    };
  }

  if (card.kind === "humidity" && card.humidity_card) {
    return {
      ...card,
      data: buildHumidityCard(HUM_BASE, card.humidity_card),
    };
  }

  return null;
};

type PropTypes = {
  id: number;
  company: BaseOption;
  portfolio: BaseOption;
  iaqDashboard: PortfolioIAQDashboard | null;
  complianceScore: PortfolioComplianceScore | null;
};

const PortfolioOverviewTab: FC<PropTypes> = ({ id, complianceScore, iaqDashboard, portfolio, company }) => {
  const callbackKey = useId();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { successfullyAddedId } = useAppSelector((store) => store.assets);
  const initialParams = QueryHelper.getFromQueryString(location.search);
  const [isShowMore, setShowMore] = useState(initialParams[QUERY_PARAMS.SHOW_MORE] === "1");
  const [selectedStatus, setSelectedStatus] = useState<StatusFilter["tone"]>("all");
  const [search, setSearch] = useState(initialParams[QUERY_PARAMS.SEARCH] || "");
  const debouncedSearch = useDebounce(search, 500);

  const cards: StatCardData[] = useMemo(
    () => [
      {
        title: "Compliance score",
        value: complianceScore?.level !== "insufficient" ? `${complianceScore?.value}%` : "-",
        description:
          complianceScore?.level !== "insufficient"
            ? `${complianceScore?.level === "high" ? "High" : complianceScore?.level === "medium" ? "Medium" : "Low"} compliance`
            : "Insufficient data",
        icon: "shield",
        tone:
          complianceScore?.level === "high"
            ? "green"
            : complianceScore?.level === "medium"
              ? "orange"
              : complianceScore?.level === "low"
                ? "red"
                : "gray",
        tooltip: "Per DM-HSD-GU119.",
      },
      {
        title: "Compl. improvement",
        value:
          complianceScore?.improvement_level !== "insufficient" ? formatImprovement(complianceScore?.improvement) : "-",
        description:
          complianceScore?.improvement_level === "insufficient" ? "Insufficient data" : "Vs previous 30 days",
        icon:
          complianceScore?.improvement_level === "up"
            ? "grow-up"
            : complianceScore?.improvement_level === "down"
              ? "grow-down"
              : complianceScore?.improvement_level === "flat"
                ? "flat"
                : undefined,
        tone:
          complianceScore?.improvement_level === "up"
            ? "green"
            : complianceScore?.improvement_level === "down"
              ? "red"
              : "gray",
        tooltip:
          "Change in the portfolio compliance score compared to the previous 30 days, across all assets in this portfolio.",
      },
      {
        title: "Avg incidents/month",
        value: complianceScore?.avg_incidents_per_month == null ? "-" : `${complianceScore.avg_incidents_per_month}`,
        description: complianceScore?.avg_incidents_per_month == null ? "Insufficient data" : "Last 3 months",
        icon: "danger",
        tone:
          complianceScore?.avg_incidents_per_month == null
            ? "gray"
            : complianceScore.avg_incidents_per_month === 0
              ? "green"
              : complianceScore.avg_incidents_per_month < 4
                ? "orange"
                : "red",
      },
      {
        title: "Total active incidents",
        value: complianceScore?.active_incidents || 0,
        description: "Open, all severities",
        icon: "danger",
        tone: (complianceScore?.active_incidents || 0) > 0 ? "red" : "green",
      },
    ],
    [complianceScore],
  );

  const statusFilters = useMemo(
    () => [
      { label: "All", count: iaqDashboard?.counts?.all || 0, tone: "all" as StatusFilter["tone"] },
      { label: "Alert", count: iaqDashboard?.counts?.alert || 0, tone: "red" as StatusFilter["tone"] },
      { label: "Warning", count: iaqDashboard?.counts?.warning || 0, tone: "orange" as StatusFilter["tone"] },
      { label: "Good", count: iaqDashboard?.counts?.good || 0, tone: "green" as StatusFilter["tone"] },
    ],
    [iaqDashboard?.counts],
  );

  const assets: AssetGroupData[] | undefined = useMemo(
    () =>
      iaqDashboard?.assets.map((item) => ({
        id: item.id.toString(),
        name: item.name || "",
        overallStatus: item.overall_status,
        status: {
          tone:
            item.overall_status === "no_data"
              ? "gray"
              : item.overall_status === "alert"
                ? "red"
                : item.overall_status === "warning"
                  ? "orange"
                  : "green",
          label:
            item.overall_status === "no_data"
              ? "No data available"
              : item.overall_status === "alert"
                ? "Alert"
                : item.overall_status === "warning"
                  ? "Warning"
                  : "Good",
        },
        image: item.avatar_url || "",
        cards: item.cards.map(adaptAssetGroupCard).filter((card): card is AdaptedAssetGroupCard => card !== null),
      })),
    [iaqDashboard?.assets],
  );

  const handleGenerateReport = () => {
    dispatch(
      showModal({
        modalType: ModalType.GENERATE_REPORT,
        modalProps: {
          level: "portfolio",
          portfolio: { value: portfolio.value, label: portfolio.label },
          company: { value: company.value, label: company.label },
        },
      }),
    );
  };

  const handleChangeStatus = (tone: StatusFilter["tone"]) => () => {
    setSelectedStatus(tone);
  };

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleShowMore = () => {
    setShowMore((prevState) => !prevState);
  };

  const handleRequestIAQDashboard = useCallback(
    (search: string, selectedStatus: StatusFilter["tone"], isShowMore: boolean, silent = false) => {
      dispatch(
        getIAQDashboardRequest(
          {
            id: Number(id),
            query: QueryHelper.getQueryString({
              ...(selectedStatus === "green" ? { [QUERY_PARAMS.STATUS]: "good" } : {}),
              ...(selectedStatus === "orange" ? { [QUERY_PARAMS.STATUS]: "warning" } : {}),
              ...(selectedStatus === "red" ? { [QUERY_PARAMS.STATUS]: "alert" } : {}),
              [QUERY_PARAMS.PAGE]: 1,
              [QUERY_PARAMS.PAGE_SIZE]: isShowMore ? 100 : 3,
              [QUERY_PARAMS.SEARCH]: (search || "")?.trim()?.toLowerCase(),
            }),
          },
          silent ? { silent: true } : undefined,
        ),
      );
    },
    [dispatch, id],
  );

  const handleSaveAsset = (values?: unknown) => {
    const data = values as AssetFormValues;

    const body: CreateAssetRequest = {
      name: data.name,
      type: data.type || null,
      description: data.description || null,
      portfolio_id: Number(id),
      manager_id: Number(data.managerId),
    };

    dispatch(createAssetRequest({ body, images: data.images }));
  };

  const handleAddAsset = () => {
    setModalCallbacks(callbackKey, { onSave: handleSaveAsset });
    dispatch(
      showModal({
        modalType: ModalType.CREATE_ASSET,
        modalProps: { mode: "create", callbackKey, companyId: company.value },
      }),
    );
  };

  useEffect(() => {
    handleRequestIAQDashboard(debouncedSearch, selectedStatus, isShowMore);
  }, [debouncedSearch, selectedStatus, isShowMore, handleRequestIAQDashboard]);

  const reload = useCallback(() => {
    handleRequestIAQDashboard(debouncedSearch, selectedStatus, isShowMore, true);
  }, [handleRequestIAQDashboard, debouncedSearch, selectedStatus, isShowMore]);

  useRealtime(["telemetry", "incidents", "compliance"], reload, { portfolioId: Number(id) });

  useEffect(() => {
    if (successfullyAddedId) {
      dispatch(hideModal());
      dispatch(resetSuccessfullyAddedAsset());
      dispatch(
        showToast({
          id: "successfully-added-asset",
          message: "The asset was successfully created.",
          type: "success",
          duration: 3000,
        }),
      );
      handleRequestIAQDashboard(debouncedSearch, selectedStatus, isShowMore);
    }
  }, [successfullyAddedId, dispatch, handleRequestIAQDashboard, debouncedSearch, selectedStatus, isShowMore]);

  useEffect(() => {
    const query = QueryHelper.getQueryString({
      [QUERY_PARAMS.TAB]: "overview",
      [QUERY_PARAMS.SEARCH]: debouncedSearch.trim() || undefined,
      [QUERY_PARAMS.SHOW_MORE]: isShowMore ? "1" : undefined,
    });
    navigate(`${PAGES.PORTFOLIO_DETAIL.replace(":id", String(id))}${query}`, { replace: true });
  }, [debouncedSearch, isShowMore, id, navigate]);

  return (
    <div className={styles.overview}>
      <div className={styles.stats}>
        {cards.map((card) => (
          <StatCard key={card.title} data={card} />
        ))}
      </div>

      <div className={styles.dashboard}>
        <div className={styles.toolbar}>
          <div className={styles.dashboardHead}>
            <div className={styles.dashboardHeading}>
              <h2 className={styles.dashboardTitle}>Indoor air quality monitoring dashboard</h2>
              <p className={styles.dashboardSubtitle}>Standard: DM-HSD-GU119, for existing buildings</p>
            </div>

            <Button variant="primary" className={styles.dashboardButton} onClick={handleGenerateReport}>
              Generate report
            </Button>
          </div>

          <div className={styles.filters}>
            <div className={styles.chips}>
              {statusFilters.map((filter) => (
                <button
                  type="button"
                  key={filter.label}
                  onClick={handleChangeStatus(filter.tone)}
                  className={classNames(styles.chip, { [styles.chipActive]: selectedStatus === filter.tone })}
                >
                  {filter.tone !== "all" && (
                    <span className={classNames(styles.chipDot, styles[`dot-${filter.tone}`])} />
                  )}
                  <span className={styles.chipLabel}>{filter.label}</span>
                  <span className={styles.chipCount}>{filter.count}</span>
                </button>
              ))}
            </div>

            <SearchInput name="search-assets" value={search} placeholder="Search assets..." onChange={handleSearch} />
          </div>
        </div>

        <div className={styles.groups}>
          {assets?.map((group) => (
            <AssetGroup key={group.id} data={group} />
          ))}

          {!assets?.length && (
            <div className={styles.empty}>
              <NotFoundIcon />

              <p>{!!search ? "No assets found" : "No assets yet"}</p>

              {!search && (
                <Button variant="secondary" onClick={handleAddAsset}>
                  Add first asset
                </Button>
              )}
            </div>
          )}

          {(iaqDashboard?.total || 0) > 3 && (
            <div className={styles.showMoreRow}>
              <button type="button" className={styles.showMore} onClick={handleShowMore}>
                {isShowMore ? "Show less" : "Show more"}
              </button>
            </div>
          )}
        </div>
      </div>

      <ComplianceTrendChart id={String(id)} type="portfolio" />

      <IncidentBarChart id={String(id)} type="portfolio" />
    </div>
  );
};

export default PortfolioOverviewTab;
